import React from 'react'
import {useLayoutEffect, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {
  allowAllOnTask,
  disallowAllOnTask,
  getDefaultTaskPermissions,
  PermissionObject,
  TaskPermissions
} from 'yougile-model'
import {LinkButtonNew} from '@components/common/base/buttons'
import {cn} from '@containers/html-utils'
import {
  PermissionsCheckbox,
  PermissionsHeader,
  PermissionsSelect
} from './permissions-settings-utils'

const TwRow = tw.div`
  px-16
  py-8
  flex
  items-center
  gap-16
  min-h-32
`

const TwAdditionalText = tw.div`
  text-sm-regular
  text-placeholder-new
`

// eslint-disable-next-line max-lines-per-function
export const PermissionsTaskSettings = (props: {
  isSub?: boolean
  object: PermissionObject
  onChange: (obj: PermissionObject) => void
  allowEmpty: boolean
}) => {
  const {object, onChange, isSub} = props
  const specify = () =>
    onChange({
      type: 'task',
      objectId: undefined,
      permissions: getDefaultTaskPermissions()
    })
  const moveOpts: Array<TaskPermissions['move']> = [
    'no',
    'board',
    'project',
    'yes'
  ]
  const assignOpts: Array<TaskPermissions['assignUsers']> = [
    'no',
    'add-self',
    'set-self',
    'change-from-self',
    'yes'
  ]
  const notifyOpts: Array<TaskPermissions['editWhoToNotify']> = [
    'no',
    'self',
    'yes'
  ]
  const editSubtasksOpts: Array<TaskPermissions['editSubtasks']> = [
    'no',
    'complete',
    'yes'
  ]
  const allowAll = () =>
    object?.type === 'task' &&
    onChange({...object, permissions: allowAllOnTask(object.permissions)})
  const disallowAll = () =>
    object?.type === 'task' &&
    onChange({...object, permissions: disallowAllOnTask(object.permissions)})

  // A hack to keep data while playing a collapsing animation
  const [realObject, setRealObject] = useState<PermissionObject>(object)
  useLayoutEffect(() => {
    if (object) {
      setRealObject(object)
    }
  }, [realObject, object])

  const listRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    if (listRef.current) {
      listRef.current.style.maxHeight = object
        ? listRef.current.scrollHeight + 'px'
        : '0'
    }
  }, [realObject, object, listRef])

  return (
    <>
      {realObject && (
        <div
          ref={listRef}
          className={cn(
            'flex flex-col gap-8 overflow-hidden [transition:max-height_.3s] max-h-0',
            !object && 'pointer-events-none'
          )}
        >
          {!isSub && (
            <PermissionsHeader
              title={Lang.msg.permissionsTask}
              allow={allowAll}
              disallow={disallowAll}
            />
          )}

          <PermissionsCheckbox {...props} object={realObject} path={['show']} />
          <PermissionsCheckbox
            {...props}
            object={realObject}
            path={['editTitle']}
          />
          <PermissionsCheckbox
            {...props}
            object={realObject}
            path={['complete']}
          />
          <PermissionsSelect
            {...props}
            object={realObject}
            path={['assignUsers']}
            options={assignOpts}
          />
          <PermissionsSelect
            {...props}
            object={realObject}
            path={['editSubtasks']}
            options={editSubtasksOpts}
          />
          <PermissionsCheckbox
            {...props}
            object={realObject}
            path={['editStickers']}
          />
          <PermissionsCheckbox
            {...props}
            object={realObject}
            path={['editPins']}
          />
          <PermissionsSelect
            {...props}
            object={realObject}
            path={['move']}
            options={moveOpts}
          />
          <PermissionsCheckbox
            {...props}
            object={realObject}
            path={['editDescription']}
          />
          <PermissionsCheckbox
            {...props}
            object={realObject}
            path={['sendMessages']}
          />
          <PermissionsCheckbox
            {...props}
            object={realObject}
            path={['sendFiles']}
          />
          <PermissionsSelect
            {...props}
            object={realObject}
            path={['editWhoToNotify']}
            options={notifyOpts}
          />
          <PermissionsCheckbox
            {...props}
            object={realObject}
            path={['close']}
          />
          <PermissionsCheckbox
            {...props}
            object={realObject}
            path={['connect']}
          />
          <PermissionsCheckbox
            {...props}
            object={realObject}
            path={['delete']}
          />

          {/*HACK: if this is removed when selecting a task as an object and
         scrolling to bottom perfect scrollbar allows overscrolling which
         shouldn't happen. The cause is unknown yet, previous cases was because
         of `border` css property, but there are no such borders. */}
          {!isSub && <TwRow />}
        </div>
      )}
      {!object && (
        <TwRow>
          <TwAdditionalText>
            {Lang.msg.permissionsSettingsTaskSame}
          </TwAdditionalText>
          <LinkButtonNew
            title={Lang.msg.permissionsSettingsTaskSameSpecify}
            onClick={specify}
            className="ml-auto"
          />
        </TwRow>
      )}
    </>
  )
}
